import React from 'react';
import { View, Pressable } from 'react-native';
import { BodyText } from '../Text';
import type { TriggerProps, TriggerSelectedOptionProps } from './Select.types';
import { styles } from './Select.styles';
import { webOnlyStyles } from '../../utils';
import { focusOnElement } from '../../utils/accessibility/focus/focusOnElement.web';
import { CollapseIndicatorIcon } from '../Collapsible';
import { LoadingPlaceholderFitToView } from '../LoadingPlaceholder';
import { FadeView } from '../FadeView';

export const Trigger = React.forwardRef<View, TriggerProps>((props, ref) => {
  const {
    selectedOption,
    opened,
    label,
    selectId,
    variation,
    isInteracting,
    testID,
    isLoading,
  } = props;
  const isBoxVariation = variation === 'box';

  const { onLayout, onPress } = props;
  const [isFirstPress, setFirstPress] = React.useState(true);
  const style = [
    styles.row,
    styles.trigger,
    isBoxVariation && styles.triggerBox,
  ];
  const handleTrigger = React.useCallback(() => {
    setFirstPress(false);
    onPress();
  }, [setFirstPress, onPress]);

  React.useEffect(() => {
    if (opened || isFirstPress) return;
    focusOnElement(ref as React.RefObject<View>);
  }, [ref, isFirstPress, opened]);

  return (
    <View style={styles.triggerWrapper}>
      <FadeView show={isInteracting} style={styles.triggerHover} />

      {isLoading ? <LoadingPlaceholderFitToView palette="cream" /> : null}

      <Pressable
        testID={testID ?? 'sg-select-trigger'}
        accessibilityRole="button"
        accessibilityLabel={`select trigger ${
          label ? `for ${label}` : ''
        }. currently selected ${selectedOption}`}
        style={style}
        ref={ref}
        accessibilityExpanded={opened}
        accessibilityControls={selectId}
        onLayout={onLayout}
        onPress={handleTrigger}
      >
        <TriggerSelectedOption
          selectedOption={selectedOption ?? ''}
          bold={!isBoxVariation}
        />
        <CollapseIndicatorIcon expanded={opened} />
      </Pressable>
    </View>
  );
});

// ─── SUBCOMPONENTS ──────────────────────────────────────────────────────────────

const TriggerSelectedOption = (props: TriggerSelectedOptionProps) => {
  const { bold, selectedOption } = props;

  return (
    <BodyText
      size={4}
      bold={bold}
      style={webOnlyStyles({ userSelect: 'none' })}
    >
      {selectedOption}
    </BodyText>
  );
};
